import { LinkProps, MoreButtonProps } from '@stadionhq-v3/base-ui';

export const stripTrailingSlash = (url: string) =>
  url.endsWith('/') ? url.slice(0, -1) : url;

export const addPrecedingSlash = (url: string) =>
  url.startsWith('/') ? url : `/${url}`;

/**
 * Expand a list of URL paths to include every parent path in the same
 * tree, deduplicated. Walks up by stripping the trailing segment until
 * only one segment remains (the locale root, e.g. `/en`).
 *
 * Used by Full Route Cache eviction + CDN purge so that publishing a
 * single entry also evicts the listing and home pages that embed it.
 * Without this, publishing `/en/news/foo` leaves `/en/news` and `/en`
 * stale even though their HTML was rebuilt by the data-cache revalidation.
 *
 * Rules:
 *   - Only paths starting with `/` are walked; other inputs (full
 *     URLs like `https://...`) pass through unchanged so admin-supplied
 *     external URLs aren't accidentally over-purged.
 *   - Query strings and hashes are stripped before the walk, but the
 *     original URL (with query/hash) is preserved in the output.
 *   - Stops at the single-segment level (`/en`), never returns `/`.
 *
 * Example:
 *   expandParentPaths(['/en/news/foo'])
 *   → ['/en/news/foo', '/en/news', '/en']
 */
export const expandParentPaths = (urls: string[]): string[] => {
  const expanded = new Set<string>();
  for (const url of urls) {
    if (typeof url === 'string' && url.length > 0) {
      expanded.add(url);
      if (url.startsWith('/')) {
        const queryStart = url.search(/[?#]/);
        let current = queryStart === -1 ? url : url.slice(0, queryStart);
        let lastSlash = current.lastIndexOf('/');
        while (lastSlash > 0) {
          current = current.slice(0, lastSlash);
          expanded.add(current);
          lastSlash = current.lastIndexOf('/');
        }
      }
    }
  }
  return [...expanded];
};

export const sanitiseLinkPropsForHTMLAnchor = <
  T extends { internalLinkData?: unknown; subPage?: unknown },
>(
  linkProps: T
): T => {
  const { internalLinkData, subPage, ...rest } = linkProps;
  // @ts-expect-error generic error
  return rest;
};

export const sanitiseNextLinkPropsForHTMLAnchor = <
  T extends { internalLinkData?: unknown; subPage?: unknown } & LinkProps,
>(
  linkProps: T
): T => {
  const { internalLinkData, subPage, useNext, scroll, ...rest } = linkProps;
  // @ts-expect-error generic error
  return rest;
};

export const sanitiseMoreButtonPropsForLinkProps = <
  T extends LinkProps = LinkProps,
>(
  linkProps: Partial<MoreButtonProps> & T
): T => {
  const {
    triggerLabel,
    triggerIcon,
    closeLabel,
    closeIcon,
    iconAfter,
    isSelected,
    desktopType,
    mobileType,
    ...rest
  } = linkProps;

  // @ts-expect-error generic error
  return rest;
};
